import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCompany } from '../components/Layout';
import api from '../utils/api';

export default function DashboardPage() {
  const { activeCompany } = useCompany();
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!activeCompany) return;
    setLoading(true);
    api.get(`/invoices?company_id=${activeCompany.id}`)
      .then(r => setInvoices(r.data))
      .finally(() => setLoading(false));
  }, [activeCompany]);

  const stats = {
    total: invoices.length,
    draft: invoices.filter(i => i.status === 'draft').length,
    sent: invoices.filter(i => i.status === 'sent').length,
    paid: invoices.filter(i => i.status === 'paid').length,
    outstanding: invoices.filter(i => i.status !== 'paid').reduce((s, i) => s + parseFloat(i.balance_due || 0), 0),
    revenue: invoices.filter(i => i.status === 'paid').reduce((s, i) => s + parseFloat(i.total || 0), 0),
  };

  const recent = invoices.slice(0, 8);
  const symbol = (inv) => inv.currency === 'INR' ? '₹' : '$';

  const statusBadge = (s) => <span className={`badge badge-${s}`}>{s}</span>;

  return (
    <div>
      <div className="page-header">
        <div>
          <h2>{activeCompany?.name || 'Dashboard'}</h2>
          <p>Overview of your invoices and revenue</p>
        </div>
        <button className="btn btn-primary" onClick={() => navigate('/invoices/new')}>
          + New Invoice
        </button>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="label">Total Invoices</div>
          <div className="value">{stats.total}</div>
          <div className="sub">{stats.draft} draft · {stats.sent} sent</div>
        </div>
        <div className="stat-card green">
          <div className="label">Paid This Year</div>
          <div className="value">{stats.paid}</div>
          <div className="sub">invoices collected</div>
        </div>
        <div className="stat-card accent">
          <div className="label">Outstanding</div>
          <div className="value">
            {activeCompany?.invoice_prefix === 'EH' ? '$' : '₹'}
            {stats.outstanding.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
          </div>
          <div className="sub">across {stats.draft + stats.sent} invoices</div>
        </div>
        <div className="stat-card">
          <div className="label">Total Revenue</div>
          <div className="value" style={{ fontSize: 18 }}>
            {activeCompany?.invoice_prefix === 'EH' ? '$' : '₹'}
            {stats.revenue.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
          </div>
          <div className="sub">from paid invoices</div>
        </div>
      </div>

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ fontSize: 16, fontWeight: 600 }}>Recent Invoices</h3>
          <button className="btn btn-outline btn-sm" onClick={() => navigate('/invoices')}>View all</button>
        </div>

        {loading ? <div className="loading">Loading...</div> : recent.length === 0 ? (
          <div className="empty-state">
            <div className="icon">🧾</div>
            <p>No invoices yet. Create your first one!</p>
            <button className="btn btn-primary" style={{ marginTop: 14 }} onClick={() => navigate('/invoices/new')}>+ New Invoice</button>
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Invoice #</th>
                <th>Client</th>
                <th>Date</th>
                <th>Amount</th>
                <th>Currency</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {recent.map(inv => (
                <tr key={inv.id}>
                  <td style={{ fontFamily: 'monospace', fontWeight: 600, fontSize: 13 }}>{inv.invoice_number}</td>
                  <td>{inv.client_name}</td>
                  <td style={{ color: '#888', fontSize: 13 }}>{inv.issue_date}</td>
                  <td style={{ fontWeight: 600 }}>{symbol(inv)}{parseFloat(inv.total).toFixed(2)}</td>
                  <td><span className={`badge badge-${inv.currency.toLowerCase()}`}>{inv.currency}</span></td>
                  <td>{statusBadge(inv.status)}</td>
                  <td>
                    <button className="btn btn-outline btn-sm" onClick={() => navigate(`/invoices/${inv.id}/edit`)}>Edit</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
